import { useMemo, useState } from 'react'
import { Checkbox, Select, StackList, StackListItem, toastError } from 'admination-react'
import { AskModal } from '@/core/components/ui/ask-modal'
import { anzeigeName, hatRueckzug, type DashboardCoaching } from '@/core/types'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'

const nachName = new Intl.Collator('de')

export type ZuruecksetzenSpalte = { id: string; dbName: string; listOnly?: boolean }

type Props = {

  coachings: readonly DashboardCoaching[]

  spalten: readonly ZuruecksetzenSpalte[]

  vorgewaehlt: readonly string[]

  zuruecksetzen: (coachingIds: readonly string[], spalte: string) => Promise<unknown>
  onDone: () => void
  onCancel: () => void
}

export function ZuruecksetzenDialog({ coachings: alle, spalten, vorgewaehlt, zuruecksetzen, onDone, onCancel }: Props) {
  const [gewaehlt, setGewaehlt] = useState<ReadonlySet<string>>(() => new Set(vorgewaehlt))
  const [spalte, setSpalte] = useState('')
  const [bestaetigen, setBestaetigen] = useState(false)
  const [busy, setBusy] = useState(false)

  const index = (stage: string) => spalten.findIndex((s) => s.id === stage)
  const coachings = useMemo(
    () => alle.filter((c) => !hatRueckzug(c) && spalten.some((s) => s.id === c.stage)).sort((a, b) => nachName.compare(anzeigeName(a), anzeigeName(b))),
    [alle, spalten],
  )
  const auswahl = coachings.filter((c) => gewaehlt.has(c.id))

  const fruehestes = Math.min(...auswahl.map((c) => index(c.stage)))
  const optionen = useMemo(
    () =>
      auswahl.length === 0 ? [] : spalten.filter((s, i) => i < fruehestes && !s.listOnly && s.id !== 'aktivierung').map((s) => ({ value: s.dbName, label: s.dbName })),
    [auswahl.length, spalten, fruehestes],
  )
  const spalteGueltig = optionen.some((o) => o.value === spalte)

  const toggle = (id: string, on: boolean) =>
    setGewaehlt((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const weiter = () => {
    if (auswahl.length === 0) return toastError("Bitte mindestens ein Coaching auswählen.")
    if (!spalteGueltig) return toastError("Bitte eine frühere Spalte auswählen.")
    setBestaetigen(true)
  }

  const bestaetigt = async () => {
    const identity = captureWriteIdentity()
    setBusy(true)
    try {
      await zuruecksetzen(auswahl.map((c) => c.id), spalte)
      if (!isWriteIdentityCurrent(identity)) return
      onDone()
    } catch (e) {
      if (!isWriteIdentityCurrent(identity)) return
      console.error("Zurücksetzen fehlgeschlagen:", e)
      toastError(friendlyErrorMessage(e, "Zurücksetzen fehlgeschlagen. Es wurde nichts geändert."))
      setBusy(false)
    }
  }

  if (bestaetigen) {
    return (
      <AskModal
        open
        icon="i-arrow-left"
        iconStroke
        variant="is-warning"
        question={`Auf „${spalte}“ zurücksetzen?`}
        description={
          <>
            <p>{auswahl.map(anzeigeName).join(', ')}</p>
            <p>
              Die Schritte ab dieser Spalte werden wieder geöffnet. Bereits erstellte Dokumente bleiben erhalten und werden als ersetzt markiert.</p>
          </>
        }
        confirmLabel="Zurücksetzen"
        cancelLabel="Abbrechen"
        busy={busy}
        onConfirm={() => void bestaetigt()}
        onCancel={() => setBestaetigen(false)}
        onDismiss={onCancel}
      />
    )
  }

  return (
    <AskModal
      open
      icon="i-arrow-left"
      iconStroke
      variant="is-info"
      question="Teilnehmende zurücksetzen"
      description={
        <>
          <StackList scroll>
            {coachings.map((c) => (
              <StackListItem key={c.id}>
                <Checkbox checked={gewaehlt.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)}>
                  {anzeigeName(c)}
                </Checkbox>
              </StackListItem>
            ))}
          </StackList>
          <Select
            options={optionen}
            value={spalte}
            onChange={setSpalte}
            placeholder="Frühere Spalte wählen"
            aria-label="Spalte"
          />
        </>
      }
      confirmLabel="Weiter"
      cancelLabel="Abbrechen"
      onConfirm={weiter}
      onCancel={onCancel}
    />
  )
}
